import { endOfDay, isAfter } from 'date-fns';
import produce, { current } from 'immer';

import {
  defaults,
  find,
  forEach,
  isArray,
  isEmpty,
  isObject,
  isString,
  maxBy,
  minBy,
  size,
  sortBy,
  values,
} from 'lodash-es';
import { CampaignType } from '../constants/postType.constants';

import { authorizedServerFetch, revalidatePage } from 'actions/server.actions';
import { uploadToCloudinary } from '../utils/cloudinary';
import getQualifiedImageUrl from '../utils/imageUrlProcessor';
import { initialPostState } from './postSlice';
import useStore from './store';
import { updatePoints } from './userActions';

const processPostData = (sourcePostData) => {
  if (sourcePostData) {
    const postData = { ...sourcePostData };
    if (isArray(postData.attachmentUrl)) {
      postData.attachmentUrl = postData.attachmentUrl.map((url) =>
        getQualifiedImageUrl(url),
      );
    } else if (isString(postData.attachmentUrl)) {
      postData.attachmentUrl = getQualifiedImageUrl(postData.attachmentUrl);
    }
    return postData;
  }
  return sourcePostData;
};

const clearCache = () => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed = initialPostState;
    }),
  );
};

const getPostsByType = (postType) => {
  const { postsByType, allFeedItems } = useStore.getState().socialFeed;
  const items = values(postsByType[postType]);
  return sortBy(items, 'order').map((item) => allFeedItems[item.postId]);
};

const fetchPosts = async (postType, storePosts = [], lastFetchedPost = {}) => {
  const { socialFeed } = useStore.getState();
  const postTypeToFetch = postType || socialFeed.currentPostType;
  const collection = socialFeed.postsByType[postTypeToFetch];

  const oldestItem = maxBy(values(collection), 'order');
  const oldestPost = oldestItem
    ? socialFeed.allFeedItems[oldestItem.postId]
    : null;

  const query = {
    limit: 10,
  };

  if (oldestPost?.updatedOn || lastFetchedPost?.updatedOn) {
    query.before = oldestPost?.updatedOn || lastFetchedPost?.updatedOn;
  }

  if (postTypeToFetch) {
    query.type = postTypeToFetch;
  }

  const { data, ok } = await authorizedServerFetch('/posts', {}, query);

  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        draft.socialFeed.isInitializedByType[postTypeToFetch] = true;
        draft.socialFeed.currentPostType = postTypeToFetch;
        const postsFromRemote = responseData?.posts || storePosts || [];
        if (size(postsFromRemote) > 0) {
          const postsCollection = draft.socialFeed.postsByType[postTypeToFetch];
          const numPosts = size(postsCollection);
          postsFromRemote.forEach((post, index) => {
            // eslint-disable-next-line no-param-reassign
            post.sourcePostData = processPostData(post.sourcePostData);
            postsCollection[post.id] = {
              order: numPosts + index,
              postId: post.id,
            };
            draft.socialFeed.allFeedItems[post.id] = post;
          });
        }
        draft.socialFeed.hasMorePostsByType[postTypeToFetch] =
          size(postsFromRemote) > 0;
      }),
    );
  }
  return getPostsByType(postTypeToFetch);
};

const getLatestPostEntry = (post) => {
  const result = { all: null };

  const { postsByType } = useStore.getState().socialFeed;
  result.all = minBy(values(postsByType.all), 'order');
  if (isObject(postsByType[post.sourcePostType])) {
    result[post.sourcePostType] = minBy(
      values(postsByType[post.sourcePostType]),
      'order',
    );
  }
  return result;
};

const addNewPostToStore = (post) => {
  useStore.setState(
    produce((draft) => {
      const processed = { ...post };
      processed.sourcePostData = processPostData(processed.sourcePostData);

      const latestItem = getLatestPostEntry(processed);
      forEach(latestItem, (latestPost, type) => {
        draft.socialFeed.postsByType[type][processed.id] = {
          order: latestPost ? latestPost.order - 1 : 0,
          postId: processed.id,
        };
      });
      draft.socialFeed.allFeedItems[processed.id] = processed;
    }),
  );
};

const updatePostInStore = (post) => {
  useStore.setState(
    produce((draft) => {
      if (draft.socialFeed.allFeedItems[post.id]) {
        const processed = { ...post };
        processed.sourcePostData = processPostData(processed.sourcePostData);

        draft.socialFeed.allFeedItems[post.id] = processed;
        draft.socialFeed.editedPostId = post.id;
      }
    }),
  );
};

const createPost = async (postData) => {
  try {
    const { ok, data } = await authorizedServerFetch('userposts', {
      method: 'POST',
      body: postData,
    });
    console.log('createPost', data);
    const { post, reward } = data?.data;
    addNewPostToStore(post);
    updatePoints(reward);
    // revalidatePage('/home');
    return { post, reward };
  } catch (err) {
    console.error('Error creating post ', err);
    return { errored: null, reward: null };
  }
};

const updatePost = async (postId, postData) => {
  try {
    const { ok, data } = await authorizedServerFetch(`userposts/${postId}`, {
      method: 'PUT',
      body: postData,
    });
    const { post } = data?.data;
    updatePostInStore(post);
    return { post };
  } catch (err) {
    console.error('Error updating post ', err);
    return { errored: null, reward: null };
  }
};

const createCelebration = async (payload, files) => {
  try {
    if (files && Array.isArray(files) && files.length > 0) {
      if (files[0].type) {
        const type = files[0].type.includes('image') ? 'image' : 'video';
        const uploadedFiles = await Promise.all(uploadToCloudinary(files));
        payload.attachmentUrl = uploadedFiles[0];
        payload.attachmentType = type;
      }
    }

    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));

    const { ok, data } = await authorizedServerFetch('celebrations', {
      method: 'POST',
      body: formData,
    });

    const { post, reward } = data?.data;

    revalidatePage('/home/');

    addNewPostToStore(post);
    updatePoints(reward);
    return { post, reward };
  } catch (error) {
    console.error(error);
    return { errored: null, reward: null };
  }
};

const updateCelebration = async (postId, payload, files) => {
  try {
    if (files && Array.isArray(files) && files.length > 0) {
      if (files[0].type) {
        const type = files[0].type.includes('image') ? 'image' : 'video';
        const uploadedFiles = await Promise.all(uploadToCloudinary(files));
        payload.attachmentUrl = uploadedFiles[0];
        payload.attachmentType = type;
      }
    }

    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));

    const { ok, data } = await authorizedServerFetch(`celebrations/${postId}`, {
      method: 'PUT',
      body: formData,
    });

    const { post, reward } = data?.data;

    updatePostInStore(post);
    if (reward) {
      updatePoints(reward);
    }

    return { post, reward };
  } catch (error) {
    console.error(error);
    return { errored: null, reward: null };
  }
};

const createCampaign = async (formData) => {
  const { ok, data } = await authorizedServerFetch(`campaigns`, {
    method: 'POST',
    body: formData,
  });

  if (ok) {
    const { post, reward } = data?.data;
    const startDate = new Date(post.sourcePostData.startDate);
    if (!isAfter(startDate, endOfDay(new Date()))) {
      addNewPostToStore(post);
    }
    updatePoints(reward);
  }
  return { data: data?.data, errored: !ok };
};

const updateCampaign = async (id, formData) => {
  const { ok, data } = await authorizedServerFetch(`campaigns/${id}`, {
    method: 'PUT',
    body: formData,
  });

  if (ok) {
    updatePostInStore(data?.data?.post);
  }
  return { data: data?.data, errored: !ok };
};

const saveCampaign = async (data, images, videos, post) => {
  const isEditMode = !!post;
  const payload = {
    postAs: data.postAs,
    targetViewers: data.targetViewers,
    targetOrgs: data.targetOrgs,
    nonProfitId: data.nonProfit[0].id,
    projectId: data.projectId || null,
    title: data.title,
    message: data.description,
    startDate: data.startDate,
    endDate: data.endDate,
    address: data.address,
    videoUrl: data.videoUrl,
    campaignType: data.campaignType,
    attachmentType: 'image',
    isFeatured: data.isFeatured,
  };

  if (data.campaignType === CampaignType.FundRaise) {
    payload.goalAmount = data.goalAmount;
  }

  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));

  if (images && isArray(images) && !isEmpty(images)) {
    images.forEach((image) => {
      if (image instanceof File) {
        formData.append('attachment', image);
      }
    });
  }

  if (videos && isArray(videos) && !isEmpty(videos)) {
    let uploadedVideos = [];
    try {
      if (videos[0].type) {
        uploadedVideos = await Promise.all(uploadToCloudinary(videos));
      } else {
        uploadedVideos = videos.map((video) => video.url);
      }
      formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
    } catch (error) {
      console.log(error);
      return {};
    }
  }

  if (isEditMode) {
    return updateCampaign(post.sourcePostData.id, formData);
  }
  return createCampaign(formData);
};

const createVolunteeringOpportunity = async (volunteeringData) => {
  const { ok, data } = await authorizedServerFetch(
    `admin/nonprofit/volunteerings`,
    {
      method: 'POST',
      body: volunteeringData,
    },
  );

  if (ok) {
    addNewPostToStore(data?.data?.post);
    updatePoints(data?.data?.reward);
    return { data: data?.data, ok: ok };
  }
  return { data: null, errored: !ok };
};

const updateVolunteeringOpportunity = async (id, volunteeringData) => {
  const path = `admin/nonprofit/volunteerings/${id}`;

  const { ok, data } = await authorizedServerFetch(path, {
    method: 'PUT',
    body: volunteeringData,
  });

  if (ok) {
    updatePostInStore(data?.data?.post);
  }
  return { data: data?.data, ok: ok };
};

const saveVolunteeringOpportunity = async (
  formValues,
  post,
  images,
  videos,
) => {
  const isNew = isEmpty(post);

  const {
    causes: selectedCauses,
    mandatorySkills,
    optionalSkills,
    imageUrl,
    ...payload
  } = formValues;

  payload.mandatorySkills = mandatorySkills.map((skill) => skill.id);
  payload.optionalSkills = optionalSkills.map((skill) => skill.id);
  payload.causes = selectedCauses.map((cause) => cause.id);

  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));

  if (images && isArray(images) && size(images) > 0) {
    images.forEach((image) => {
      if (image instanceof File) {
        formData.append('image', image);
      }
    });
  }

  if (videos && isArray(videos) && size(videos) > 0) {
    let uploadedVideos = [];
    if (videos[0].type) {
      try {
        uploadedVideos = await Promise.all(uploadToCloudinary(videos));
        formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
      } catch (error) {
        console.log(error);
        return {};
      }
    } else {
      uploadedVideos = videos.map((video) => video.url);
      formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
    }
  }

  if (isNew) {
    return createVolunteeringOpportunity(formData);
  }

  return updateVolunteeringOpportunity(
    post.sourcePostData?.volunteeringOpportunity?.id ||
      post.sourcePostData?.volunteerOpportunity.id,
    formData,
  );
};

const createProject = async (volunteeringData) => {
  const { ok, data } = await authorizedServerFetch(`admin/nonprofit/projects`, {
    method: 'POST',
    body: volunteeringData,
  });

  if (ok) {
    addNewPostToStore(data?.data?.post);
    updatePoints(data?.data?.reward);
    return { data: data?.data, ok: ok };
  }
  return { data: null, errored: !ok };
};

const updateProject = async (id, projectData) => {
  const path = `admin/nonprofit/projects/${id}`;

  const { ok, data } = await authorizedServerFetch(path, {
    method: 'PUT',
    body: projectData,
  });

  if (ok) {
    updatePostInStore(data?.data?.post);
  }
  return { data: data?.data, ok: ok };
};

const saveProject = async (formValues, post, images, videos) => {
  const isNew = isEmpty(post);

  const formData = new FormData();
  formData.append('data', JSON.stringify(formValues));

  if (images && isArray(images) && size(images) > 0) {
    images.forEach((image) => {
      if (image instanceof File) {
        formData.append('imageUrl', image);
      }
    });
  }

  if (videos && isArray(videos) && size(videos) > 0) {
    let uploadedVideos = [];
    if (videos[0].type) {
      try {
        uploadedVideos = await Promise.all(uploadToCloudinary(videos));
        formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
      } catch (error) {
        console.log(error);
        return {};
      }
    } else {
      uploadedVideos = videos.map((video) => video.url);
      formData.append('attachmentVideos', JSON.stringify(uploadedVideos));
    }
  }

  if (isNew) {
    return createProject(formData);
  }

  return updateProject(post.sourcePostData.id, formData);
};

const updateSourcePostData = async (postId, sourcePostData) => {
  useStore.setState(
    produce((draft) => {
      const processed = processPostData(sourcePostData);
      const updatedData = defaults(
        processed,
        draft.socialFeed.allFeedItems[postId].sourcePostData,
      );
      const post = {
        ...draft.socialFeed.allFeedItems[postId],
        sourcePostData: updatedData,
      };

      draft.socialFeed.allFeedItems[postId] = post;
    }),
  );
};

const useFilterPostsByType = (postType) => {
  const { postsByType, allFeedItems } = useStore((state) => state.socialFeed);
  const items = values(postsByType[postType]);
  return sortBy(items, 'order').map((item) => allFeedItems[item.postId]);
};

const usePostFromCache = (postId) => {
  const allFeedItems = useStore((state) => state.socialFeed.allFeedItems);
  return allFeedItems[postId] || null;
};

const getPostById = async (postId) => {
  const fromCache = useStore.getState().socialFeed.allFeedItems[postId];
  if (fromCache) {
    return fromCache;
  }
  const { ok, data } = await authorizedServerFetch(`/posts/${postId}`);

  const post = data?.data;
  if (ok && post) {
    post.sourcePostData = processPostData(post.sourcePostData);
    useStore.setState(
      produce((draft) => {
        draft.socialFeed.allFeedItems[postId] = post;
        draft.socialFeed.postsByType.all[postId] = {
          order: size(draft.socialFeed.postsByType.all),
          postId: postId,
        };
        if (draft.socialFeed.postsByType[post.sourcePostType]) {
          draft.socialFeed.postsByType[post.sourcePostType][postId] = {
            order: size(draft.socialFeed.postsByType[post.sourcePostType]),
            postId: postId,
          };
        }
      }),
    );
  }

  return post;
};

const toggleLike = async (postId, isLiked, likeType) => {
  const routePart = isLiked ? 'like' : 'unlike';
  const path = `/posts/${postId}/${routePart}`;
  let toRevert = null;
  // do an optimistic like
  useStore.setState(
    produce((draft) => {
      const cachedPost = draft.socialFeed.allFeedItems[postId];
      if (cachedPost) {
        toRevert = current(cachedPost.likeInfo);
        cachedPost.likeInfo = {
          liked: isLiked,
          likeType: isLiked ? likeType : null,
        };
        // cachedPost.totalReactions = Math.max(
        //   cachedPost.totalReactions + inc,
        //   0
        // );

        // const reactions = cachedPost.reactions || {};
        // if (isPreviouslyLiked && isLiked) {
        //   reactions[toRevert.likeInfo.likeType] = Math.max((reactions[toRevert.likeInfo.likeType] || 0) - 1, 0);
        // }

        // if (isLiked) {
        //   reactions[likeType] = (reactions[likeType] || 0) + 1;
        // } else {
        //   reactions[likeType] = Math.max((reactions[likeType] || 0) - 1, 0);
        // }

        // cachedPost.reactions = reactions;
      }
    }),
  );

  const payload = {
    as: useStore.getState().user.profile.selectedProfile,
    likeType: likeType,
  };
  const { ok, data } = await authorizedServerFetch(path, {
    method: 'POST',
    body: payload,
  });
  const responseData = data?.data;

  if (responseData) {
    updatePoints(responseData.reward);
    useStore.setState(
      produce((draft) => {
        const cachedPost = draft.socialFeed.allFeedItems[postId];
        if (cachedPost) {
          cachedPost.likeInfo = responseData.likeInfo;
          cachedPost.totalReactions = responseData.totalReactions;
          cachedPost.reactions = responseData.reactions;
        }
      }),
    );
  } else if (!ok) {
    console.error('Error liking post');
    // undo optimistic like
    useStore.setState(
      produce((draft) => {
        const cachedPost = draft.socialFeed.allFeedItems[postId];
        if (cachedPost) {
          cachedPost.likeInfo = toRevert;
        }
      }),
    );
  }
};

const applyAsVolunteer = async (payload, opportunity) => {
  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));

  const { ok, data } = await authorizedServerFetch(`volunteering/apply`, {
    method: 'POST',
    body: formData,
  });
  const responseData = data?.data;
  if (ok) {
    useStore.setState(
      produce((draft) => {
        const post = draft.socialFeed.allFeedItems[opportunity.postId];
        if (post) {
          post.sourcePostData.volunteeringOpportunity.userVolunteeringInfo =
            responseData;
        }
      }),
    );
  }
  return { errored: !ok };
};

const submitVolunteeringEffort = async (payload, opportunity) => {
  const formData = new FormData();
  formData.append('data', JSON.stringify(payload));

  const { ok, data } = await authorizedServerFetch(
    `volunteering/${opportunity.id}/efforts`,
    {
      method: 'POST',
      body: formData,
    },
  );
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const effortsSoFar = draft.user.profile.volunteeringHrs || 0;
        draft.user.profile.volunteeringHrs = effortsSoFar + payload.effort;
      }),
    );
    updatePoints(responseData.reward);
  }
  return { data: responseData, errored: !ok };
};

const fetchUserPosts = async (userId, isCurrentUser) => {
  const { ok, data } = await authorizedServerFetch(
    `/posts/users/${isCurrentUser ? 'me' : userId}`,
  );
  const responseData = data?.data;
  if (!ok) {
    return [];
  }

  const myActivityPosts = [];
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.isInitializedByType.myActivity = true;
      draft.socialFeed.postsByType.myActivity = {};

      const postsFromRemote = responseData || [];
      if (size(postsFromRemote) > 0) {
        postsFromRemote.forEach((post, index) => {
          const processed = {
            ...post,
            sourcePostData: processPostData(post.sourcePostData),
          };

          draft.socialFeed.allFeedItems[processed.id] = processed;
          draft.socialFeed.postsByType.myActivity[processed.id] = {
            order: index,
            postId: processed.id,
          };
          myActivityPosts.push(processed);
        });
      }
      draft.socialFeed.hasMorePostsByType.myActivity = false;
    }),
  );

  return myActivityPosts;
};

const fetchNonProfitPosts = async (nonProfitId) => {
  const { ok, data } = await authorizedServerFetch(
    `nonprofits/${nonProfitId}/posts?limit=100`,
  );
  const responseData = data?.data;
  if (!ok) {
    return [];
  }

  const nonProfitActivityPosts = [];
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.isInitializedByType.nonProfitActivity = true;
      draft.socialFeed.postsByType.nonProfitActivity = {};

      const postsFromRemote = responseData.posts || [];
      if (size(postsFromRemote) > 0) {
        postsFromRemote.forEach((post, index) => {
          const processed = {
            ...post,
            sourcePostData: processPostData(post.sourcePostData),
          };

          draft.socialFeed.allFeedItems[processed.id] = processed;
          draft.socialFeed.postsByType.nonProfitActivity[processed.id] = {
            order: index,
            postId: processed.id,
          };
          nonProfitActivityPosts.push(processed);
        });
      }
      draft.socialFeed.hasMorePostsByType.nonProfitActivity = false;
    }),
  );

  return nonProfitActivityPosts;
};

const initiateSendPost = async (postId) => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.sendingPostId = postId;
    }),
  );
};

const initiateDonateInKind = async (postId) => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.inKindDonationTrigger.postId = postId;
      draft.socialFeed.inKindDonationTrigger.mode = 'donate';
    }),
  );
};

const initiateInKindDeliveryConfirmation = async (postId, donationId) => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.inKindDonationTrigger.postId = postId;
      draft.socialFeed.inKindDonationTrigger.mode = 'confirm-delivery';
      draft.socialFeed.inKindDonationTrigger.donationId = donationId;
    }),
  );
};

const triggerPostActionMenu = async (anchor, postId) => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.actionTrigger = {
        anchor: anchor,
        post: draft.socialFeed.allFeedItems[postId],
      };
    }),
  );
};

const triggerCommentActionMenu = async (anchor, comment) => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.commentActionTrigger = {
        anchor: anchor,
        comment: comment,
      };
    }),
  );
};

const hidePostActionMenu = () => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.actionTrigger = {
        anchor: null,
        post: null,
      };
    }),
  );
};

const hideCommentActionMenu = () => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.commentActionTrigger = {
        anchor: null,
        comment: null,
      };
    }),
  );
};

const reportPost = async (postId) => {
  const { ok, data } = await authorizedServerFetch(`/posts/${postId}/report`, {
    method: 'POST',
    body: {},
  });
  const responseData = data?.data;
  if (ok) {
    useStore.setState(
      produce((draft) => {
        draft.socialFeed.reportedPostId = postId;
      }),
    );
  }
};

const deletePost = async (postId) => {
  const { ok, data } = await authorizedServerFetch(`/posts/${postId}`, {
    method: 'DELETE',
    body: {},
  });
  const responseData = data?.data;
  if (ok) {
    useStore.setState(
      produce((draft) => {
        draft.socialFeed.deletedPostId = postId;
      }),
    );
  }
};

const markPostAsFeatured = async (postId, featured) => {
  const { ok, data } = await authorizedServerFetch(`/posts/${postId}/feature`, {
    method: 'POST',
    body: { featured: featured },
  });
  if (ok) {
    const { data: responseData } = data;
    const { isFeatured, publicShares } = responseData;

    useStore.setState(
      produce((draft) => {
        if (draft.socialFeed.allFeedItems[postId]) {
          draft.socialFeed.allFeedItems[postId].isFeatured = isFeatured;
          draft.socialFeed.allFeedItems[postId].publicShares = publicShares;
        }
      }),
    );
  }
  return data;
};

const markPostAsPublic = async (postId, isPublic) => {
  const { ok, data } = await authorizedServerFetch(`/posts/${postId}/public`, {
    method: 'POST',
    body: { isPublic: isPublic },
  });

  if (ok) {
    const { data: responseData } = data;
    const { isFeatured, targetViewers, targetOrgs, publicShares } =
      responseData;
    useStore.setState(
      produce((draft) => {
        if (draft.socialFeed.allFeedItems[postId]) {
          draft.socialFeed.allFeedItems[postId].targetViewers = targetViewers;
          draft.socialFeed.allFeedItems[postId].targetOrgs = targetOrgs;
          draft.socialFeed.allFeedItems[postId].isFeatured = isFeatured;
          draft.socialFeed.allFeedItems[postId].publicShares = publicShares;
        }
      }),
    );
  }
  return data;
};

const deleteComment = async (postId, commentId, parentCommentId = null) => {
  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments/${commentId}`,
    {
      method: 'DELETE',
      body: {},
    },
  );
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const postComments = draft.socialFeed.commentsByPost[postId];

        if (parentCommentId) {
          const parentComment = postComments.find(
            (c) => c.id === parentCommentId,
          );
          parentComment.replies = parentComment.replies.filter(
            (c) => c.id !== commentId,
          );
          parentComment.totalReplies = Math.max(
            0,
            parentComment.totalReplies - 1,
          );
        } else {
          draft.socialFeed.commentsByPost[postId] = postComments.filter(
            (c) => c.id !== commentId,
          );
        }

        draft.socialFeed.allFeedItems[postId].totalComments = Math.max(
          0,
          draft.socialFeed.allFeedItems[postId].totalComments -
            responseData.commentsDeleted,
        );
      }),
    );
  }
};

const reportComment = async (postId, commentId, parentCommentId = null) => {
  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments/${commentId}/report`,
    {
      method: 'POST',
      body: {},
    },
  );
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const postComments = draft.socialFeed.commentsByPost[postId];
        if (parentCommentId) {
          const parentComment = postComments.find(
            (c) => c.id === parentCommentId,
          );
          parentComment.replies = parentComment.replies.filter(
            (c) => c.id !== commentId,
          );
        } else {
          draft.socialFeed.commentsByPost[postId] = postComments.filter(
            (c) => c.id !== commentId,
          );
        }
      }),
    );
  }
};

const clearEditedPostId = () => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.editedPostId = null;
    }),
  );
};

const clearDeletedPostId = () => {
  useStore.setState(
    produce((draft) => {
      if (
        draft.socialFeed.deletedPostId &&
        draft.socialFeed.allFeedItems[draft.socialFeed.deletedPostId]
      ) {
        delete draft.socialFeed.allFeedItems[draft.socialFeed.deletedPostId];

        forEach(draft.socialFeed.postsByType, (v, k) => {
          if (v[draft.socialFeed.deletedPostId]) {
            delete draft.socialFeed.postsByType[k][
              draft.socialFeed.deletedPostId
            ];
          }
        });
        // TODO fetch next page of posts if required
        // if (size(draft.socialFeed.allPosts) < 10 && draft.socialFeed.hasMorePosts) {
        //   fetchPosts();
        // }
      }
      draft.socialFeed.deletedPostId = null;
    }),
  );
};

const clearReportedPostId = () => {
  useStore.setState(
    produce((draft) => {
      if (
        draft.socialFeed.reportedPostId &&
        draft.socialFeed.allFeedItems[draft.socialFeed.reportedPostId]
      ) {
        const post =
          draft.socialFeed.allFeedItems[draft.socialFeed.reportedPostId];
        delete draft.socialFeed.allFeedItems[draft.socialFeed.reportedPostId];
        delete draft.socialFeed.postsByType.all[
          draft.socialFeed.reportedPostId
        ];
        if (
          draft.socialFeed.postsByType[post.sourcePostType] &&
          draft.socialFeed.postsByType[post.sourcePostType][
            draft.socialFeed.reportedPostId
          ]
        ) {
          delete draft.socialFeed.postsByType[post.sourcePostType][
            draft.socialFeed.reportedPostId
          ];
        }
        // TODO fetch next page of posts if required
        // if (size(draft.socialFeed.allPosts) < 10 && draft.socialFeed.hasMorePosts) {
        //   fetchPosts();
        // }
      }
      draft.socialFeed.reportedPostId = null;
    }),
  );
};

const triggerEditComment = async (commentId) => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.editingCommentId = commentId;
    }),
  );
};
const triggerEditEnd = async () => {
  useStore.setState(
    produce((draft) => {
      draft.socialFeed.editingCommentId = null;
    }),
  );
};

const createPostAttachment = async (postId, file) => {
  const formData = new FormData();
  formData.append('file', file);

  const { ok, data } = await authorizedServerFetch(
    `posts/${postId}/comments/attachment`,
    {
      method: 'POST',
      body: formData,
    },
  );
  const responseData = data?.data;

  if (ok) {
    return responseData.url;
  }
  return { errored: ok };
};

const createPostComment = async (postId, text, attachment) => {
  const attachmentUrl =
    attachment instanceof File
      ? await createPostAttachment(postId, attachment)
      : attachment;
  const commentData = {
    postId: postId,
    message: text,
    attachmentUrl: attachmentUrl,
    postAs: useStore.getState().user.profile.selectedProfile,
  };

  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments`,
    {
      method: 'POST',
      body: commentData,
    },
  );
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        if (draft.socialFeed.commentsByPost[postId]) {
          draft.socialFeed.commentsByPost[postId].unshift(responseData.comment);
        } else {
          draft.socialFeed.commentsByPost[postId] = [responseData.comment];
        }
        draft.socialFeed.allFeedItems[postId].totalComments += 1;
      }),
    );
    return responseData;
  }
  return { errored: ok };
};

const updateComment = async (
  postId,
  commentId,
  text,
  parentCommentId,
  attachment,
) => {
  const attachmentUrl =
    attachment instanceof File
      ? await createPostAttachment(postId, attachment)
      : attachment;
  const commentData = {
    postId: postId,
    commentId: commentId,
    message: text,
    attachmentUrl: attachmentUrl,
    postAs: useStore.getState().user.profile.selectedProfile,
  };

  triggerEditEnd();

  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments/${commentId}`,
    {
      method: 'PUT',
      body: commentData,
    },
  );
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const index = draft.socialFeed.commentsByPost[postId].findIndex(
          (c) => c.id === commentId,
        );

        draft.socialFeed.commentsByPost[postId][index] = responseData;

        if (parentCommentId) {
          const indexi = draft.socialFeed.commentsByPost[postId].findIndex(
            (c) => c.id === parentCommentId,
          );

          const indexToEdit = draft.socialFeed.commentsByPost[postId][
            indexi
          ].replies.findIndex((c) => c.id === commentId);

          draft.socialFeed.commentsByPost[postId][indexi].replies[indexToEdit] =
            responseData;
        }
      }),
    );

    return responseData;
  }
  return { errored: ok };
};

const replyToPostComment = async (
  postId,
  text,
  attachment,
  parentCommentId,
) => {
  const attachmentUrl =
    attachment instanceof File
      ? await createPostAttachment(postId, attachment)
      : attachment;
  const commentData = {
    postId: postId,
    message: text,
    attachmentUrl: attachmentUrl,
    postAs: useStore.getState().user.profile.selectedProfile,
  };

  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments/${parentCommentId}`,
    {
      method: 'POST',
      body: commentData,
    },
  );
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const commentThread = find(draft.socialFeed.commentsByPost[postId], {
          id: parentCommentId,
        });
        if (!isEmpty(commentThread)) {
          if (!commentThread.replies) {
            commentThread.replies = [];
          }
          commentThread.replies.push(...responseData.comment.replies);
          commentThread.totalReplies = responseData.comment.totalReplies;
          draft.socialFeed.allFeedItems[postId].totalComments += 1;
        }
      }),
    );
    return responseData;
  }
  return { errored: ok };
};

const getLatestCommentForPost = (postId) => {
  const comments = useStore.getState().socialFeed.commentsByPost[postId];
  if (!isEmpty(comments)) {
    return comments[0];
  }
  return null;
};

const fetchComments = async (postId, limit, ignoreCache, olderThan) => {
  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments`,
    {},
    {
      before: olderThan,
      limit: limit,
    },
  );
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        if (ignoreCache) {
          draft.socialFeed.commentsByPost[postId] = [];
        }
        if (draft.socialFeed.commentsByPost[postId]) {
          draft.socialFeed.commentsByPost[postId] = [
            ...draft.socialFeed.commentsByPost[postId],
            ...responseData.comments,
          ];
        } else {
          draft.socialFeed.commentsByPost[postId] = responseData.comments;
        }
        draft.socialFeed.hasMoreCommentsByPost[postId] = responseData.hasMore;

        responseData.comments.forEach((comment) => {
          const numReplies = size(comment.replies);
          if (numReplies > 0) {
            draft.socialFeed.hasMoreRepliesByComment[comment.id] =
              numReplies < comment.totalReplies;
          } else {
            draft.socialFeed.hasMoreRepliesByComment[comment.id] = false;
          }
        });
      }),
    );
    return responseData;
  }
  return { errored: ok };
};

const fetchReplies = async (postId, commentId, olderThan, limit) => {
  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments/${commentId}`,
    {},
    {
      before: olderThan,
      limit: limit,
    },
  );
  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const commentThread = find(draft.socialFeed.commentsByPost[postId], {
          id: commentId,
        });
        if (!isEmpty(commentThread)) {
          if (!commentThread.replies) {
            commentThread.replies = [];
          }
          commentThread.replies = [
            ...responseData.replies,
            ...commentThread.replies,
          ];

          draft.socialFeed.hasMoreRepliesByComment[commentId] =
            responseData.hasMore;
        }
      }),
    );
    return responseData;
  }
  return { errored: ok };
};

const isPostAuthor = (comment) => {
  const post = useStore.getState().socialFeed.allFeedItems[comment.postId];

  return (
    post &&
    post.author &&
    post.author.id === comment.authorId &&
    post.postAs === comment.postAs
  );
};

const findComment = (comments, comment) => {
  const parentComment = comment.parentCommentId
    ? find(comments, { id: comment.parentCommentId })
    : comment;
  if (parentComment) {
    const collection = comment.parentCommentId
      ? parentComment.replies
      : comments;
    return find(collection, { id: comment.id });
  }
  return null;
};

const setPostCommentLike = async (postId, comment, likeType) => {
  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments/${comment.id}/like`,
    {
      method: 'POST',
      body: JSON.stringify({
        likeType: likeType,
        as: useStore.getState().user.profile.selectedProfile,
      }),
    },
  );

  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const cached = findComment(
          draft.socialFeed.commentsByPost[postId],
          comment,
        );
        if (!isEmpty(cached)) {
          cached.reactions = responseData.reactions;
          cached.likeInfo = {
            likeType: likeType,
            liked: true,
          };
        }
      }),
    );
    return responseData;
  }
  return { errored: ok };
};

const setPostCommentUnlike = async (postId, comment) => {
  const { ok, data } = await authorizedServerFetch(
    `/posts/${postId}/comments/${comment.id}/unlike`,
    {
      method: 'POST',
      body: JSON.stringify({
        as: useStore.getState().user.profile.selectedProfile,
      }),
    },
  );

  const responseData = data?.data;

  if (ok) {
    useStore.setState(
      produce((draft) => {
        const cached = findComment(
          draft.socialFeed.commentsByPost[postId],
          comment,
        );
        if (!isEmpty(cached)) {
          if (cached.likeInfo) {
            cached.reactions = responseData.reactions;
            cached.likeInfo = {
              likeType: null,
              liked: false,
            };
          }
        }
      }),
    );
    return responseData;
  }
  return { errored: ok };
};

const fetchMentionedUsers = async (userIds) => {
  const cached = useStore.getState().socialFeed.mentionedUsers;
  const idsToFetch = userIds.filter((id) => !cached[id]);
  if (idsToFetch.length > 0) {
    const { ok, data } = await authorizedServerFetch(
      `/users/mention`,
      {},
      {
        ids: idsToFetch.join(','),
      },
    );
    const responseData = data?.data;

    if (ok) {
      useStore.setState(
        produce((draft) => {
          forEach(responseData, (user) => {
            draft.socialFeed.mentionedUsers[user.id] = user;
          });
        }),
      );
    }
  }
  const cachedMentions = useStore.getState().socialFeed.mentionedUsers;
  return userIds.map((id) => cachedMentions[id]).filter((user) => !!user);
};

export {
  applyAsVolunteer,
  clearCache,
  clearDeletedPostId,
  clearEditedPostId,
  clearReportedPostId,
  createCelebration,
  createPost,
  createPostAttachment,
  createPostComment,
  deleteComment,
  deletePost,
  fetchComments,
  fetchMentionedUsers,
  fetchNonProfitPosts,
  fetchPosts,
  fetchReplies,
  fetchUserPosts,
  getLatestCommentForPost,
  getPostById,
  getPostsByType,
  hideCommentActionMenu,
  hidePostActionMenu,
  initiateDonateInKind,
  initiateInKindDeliveryConfirmation,
  initiateSendPost,
  isPostAuthor,
  markPostAsFeatured,
  markPostAsPublic,
  replyToPostComment,
  reportComment,
  reportPost,
  saveCampaign,
  saveVolunteeringOpportunity,
  saveProject,
  setPostCommentLike,
  setPostCommentUnlike,
  submitVolunteeringEffort,
  toggleLike,
  triggerCommentActionMenu,
  triggerEditComment,
  triggerEditEnd,
  triggerPostActionMenu,
  updateCelebration,
  updateComment,
  updatePost,
  updatePostInStore,
  updateSourcePostData,
  useFilterPostsByType,
  usePostFromCache,
};
